import type { CommunityId } from '../sim/species';
import { plainTitle } from './surveyNames';
import type { CommunitySummary, GuideEntry, Indicator, IndicatorId, KnowledgeState, Level, ManagementReadout, Priority, SpeciesDetail, Trend } from './overview';
import { escapeHtml } from './format';
import { dollars as dollarsOf } from '../sim/operations';
import { ISLAND_NAMES, type ReefIsland } from '../sim/reef';

/**
 * The sanctuary card's layers (M160, the user's brief and choices; `GDD.md` section 5): the overview in place of the
 * card's rows, each indicator, community and species one tap further down, the conditions and the aquarium's rows
 * kept beneath, and the field guide. Each a page of the card, drawn from `overview.ts`'s read-outs, which hold every
 * figure; here only the markup, so a page is tested without a browser.
 */

/** A page of the card: the overview, or one of the layers under it. */
export type CardView =
  | { kind: 'overview' }
  | { kind: 'indicator'; id: IndicatorId }
  | { kind: 'community'; id: CommunityId }
  | { kind: 'species'; id: string }
  | { kind: 'conditions' }
  | { kind: 'management' }
  | { kind: 'operations' }
  | { kind: 'campaign' }
  | { kind: 'review' }
  | { kind: 'guide' }
  // M262: the old overview as Sanctuary details, Manage's home now the overview; the first loop's own pages.
  | { kind: 'details' }
  | { kind: 'coral' }
  | { kind: 'survey' }
  | { kind: 'findings' }
  | { kind: 'respond' }
  // M274: a management area's card, the work under way and planned, and the month's report.
  | { kind: 'area'; id: ReefIsland }
  | { kind: 'plan' }
  | { kind: 'report' }
  // M277: what can be built or bought.
  | { kind: 'build' }
  // M287: every island's measured reef health over time.
  | { kind: 'health' }
  // M289: the opening's first survey, a choice.
  | { kind: 'first-survey' }
  // M289: a month's summary at its close.
  | { kind: 'month' }
  // M320: a result home, over the game.
  | { kind: 'result' };

/** A level as the player reads it, beside its color (`style.css`'s `.level-*`). */
export const LEVEL_WORDS: Record<Level, string> = { good: 'Good', fair: 'Fair', poor: 'Poor', unknown: 'Not known' };

/** A trend as an arrow and its words, for the arrow's reader. */
const TREND: Record<Trend, [string, string]> = { up: ['↑', 'rising'], down: ['↓', 'falling'], steady: ['→', 'steady'], unknown: ['', 'trend not known'] };

/** What is known of a species, in the brief's four states. */
const KNOWN_WORDS: Record<KnowledgeState, string> = { monitored: 'Monitored', estimated: 'Estimated', present: 'Present', 'not surveyed': 'Not surveyed' };

/** A button that opens a page (`data-act="open"`, its kind and id read back by the HUD). */
function opens(view: CardView, inner: string, cls: string): string {
  return `<button class="${cls}" data-act="open" data-kind="${view.kind}"${'id' in view ? ` data-id="${escapeHtml(view.id)}"` : ''}>${inner}</button>`;
}

function trendHtml(trend: Trend): string {
  const [arrow, words] = TREND[trend];
  return arrow ? `<span class="trend" title="${words}" aria-label="${words}">${arrow}</span>` : '';
}

/** A level's words; an unknown one's why where it is given (M237: "Survey needed", "Baseline incomplete", ...). */
function levelHtml(level: Level, unknownAs?: string): string {
  return `<span class="level level-${level.replace(' ', '-')}">${escapeHtml(level === 'unknown' && unknownAs ? unknownAs : LEVEL_WORDS[level])}</span>`;
}

/** The page's way back, and where it goes back to. */
export function backHtml(to: string): string {
  return `<p class="crumbs"><button class="back" data-act="back">‹ ${escapeHtml(to)}</button></p>`;
}

/**
 * The overview (the brief's first layer): the six indicators as tiles, each opening its own page; the priorities of
 * the moment, two to four, none where nothing calls for the player; the communities at the sanctuary, each opening
 * its page; and the conditions and the field guide.
 */
export function overviewHtml(indicators: readonly Indicator[], priorities: readonly Priority[], communities: readonly CommunitySummary[], managed = false): string {
  const tiles = indicators.map((i) => opens({ kind: 'indicator', id: i.id }, `<span class="tile-label">${escapeHtml(i.label)}</span><span class="tile-value">${escapeHtml(i.value)} ${trendHtml(i.trend)}</span>${levelHtml(i.level, i.unknownAs)}`, `tile level-edge-${i.level}`)).join('');
  const now = priorities.length > 0
    ? `<h3>Now</h3><ul class="priorities">${priorities.map((p) => `<li class="priority ${p.severity}"><strong>${escapeHtml(plainTitle(p.title))}</strong> ${escapeHtml(p.detail)}</li>`).join('')}</ul>`
    : '';
  const list = communities.map((c) => `<li>${opens({ kind: 'community', id: c.id }, `<span class="row-name">${escapeHtml(c.name)}</span>${levelHtml(c.level, c.unknownAs)} ${trendHtml(c.trend)}`, 'row')}</li>`).join('');
  return `<div class="tiles">${tiles}</div>${now}<h3>Communities</h3><ul class="rows">${list}</ul>
    <p class="more">${managed ? `${opens({ kind: 'management' }, 'Management', 'link')} ` : ''}${opens({ kind: 'conditions' }, 'Conditions and the aquarium', 'link')} ${opens({ kind: 'guide' }, 'Field guide', 'link')}</p>`;
}

/** A dropdown of a management page (`data-act` its command, `data-id` what it sets), its choice in force selected. */
function choose(act: string, id: string, label: string, choices: readonly string[], on: number): string {
  // Buttons, one a choice, the one in force pressed (M238, the overhaul's brief: no dropdowns).
  return `<span class="choices" role="group" aria-label="${escapeHtml(label)}">${choices.map((c, i) => `<button class="choice" data-act="${act}" data-id="${escapeHtml(id)}" data-option="${i}" aria-pressed="${i === on}">${escapeHtml(c)}</button>`).join('')}</span>`;
}

/**
 * The management tools' page (review M181: built in the simulation, nothing on screen set them; the user's design,
 * M165: "a single fishing-management panel with a simple geographic scope selector"): each island's Herbivore
 * Protection Area and its patrols, and each fish group's bag limit and minimum size on all four islands, beside
 * Hawaiʻi's own. Each control a command (`protect_herbivores`, `set_patrol`, `set_limit`), sent by the HUD; every
 * figure `managementOf`'s.
 */
/**
 * The management page (M165, M170, M171; M238: the rules proposed, the patrols paid for): each island's Herbivore
 * Protection Area and each fish group's limits a State rule the sanctuary proposes, its proposal under way told where
 * it stands (`pending`, by `protect-<island>` and `limit-<group>-<bag|size>`); each island's patrols the officers' hours
 * the conservancy buys, with their cost a year.
 */
export function managementHtml(m: ManagementReadout, pending: Readonly<Record<string, string>> = {}): string {
  const percent = (x: number): string => `${Math.round(100 * x)}%`;
  const note = (key: string): string => (pending[key] ? `<span class="row-note pending">${escapeHtml(pending[key] as string)}</span>` : '');
  const areas = m.islands.map((x) => `<li class="manage-row"><span class="row-name">${escapeHtml(x.name)}</span>
    <button class="toggle" data-act="protect" data-id="${x.island}" aria-pressed="${x.herbivoresProtected}"${pending[`protect-${x.island}`] ? ' disabled' : ''}>${x.herbivoresProtected ? 'Protected' : 'Not protected'}</button>
    <span class="row-note">${x.reefKm2.toFixed(0)} km² of reef, ${percent(x.share)} of Maui Nui's</span>${note(`protect-${x.island}`)}</li>`).join('');
  const patrols = m.islands.map((x) => `<li class="manage-row"><span class="row-name">${escapeHtml(x.name)}</span>
    ${choose('patrol', x.island, `${x.name}'s patrols`, x.patrol.choices.map((c, level) => `${capital(c)}: ${percent(x.patrol.takenAnyway[level] as number)} taken anyway${level > 0 ? `, ${dollarsOf(x.patrol.costPerYear[level] as number)} a year` : ', no cost'}`), x.patrol.level)}</li>`).join('');
  const limits = m.limits.map((g) => `<li class="manage-row"><span class="row-name">${escapeHtml(capital(g.name))}</span>
    <span class="row-note">Hawaiʻi's: ${escapeHtml(g.hawaii)}</span>
    ${g.bag.choices.length > 1 ? `<div class="manage-choice">Bag ${choose('limit', `${g.group}-bag`, `${g.name}'s bag limit`, g.bag.choices, g.bag.option)}${note(`limit-${g.group}-bag`)}</div>` : ''}
    ${g.size.choices.length > 1 ? `<div class="manage-choice">Size ${choose('limit', `${g.group}-size`, `${g.name}'s minimum size`, g.size.choices, g.size.option)}${note(`limit-${g.group}-size`)}</div>` : ''}</li>`).join('');
  return `<h3>Management</h3>
    <p class="why">Rules are the State's to make. The sanctuary proposes one: its liaison consults the island's communities, about four months, then the State's rule-making takes about seven. Patrols are officers' hours the conservancy pays for, day by day.</p>
    <h4>Herbivore Protection Areas</h4>
    <p class="note">On a protected island's reef no parrotfish (uhu), kala, nenue or manini may be taken. Protected now: ${percent(m.protectedShare)} of the reef.</p>
    <ul class="rows">${areas}</ul>
    <h4>Patrols</h4>
    <p class="note">What a rule forbids is still taken in part, less as the patrols are closer.</p>
    <ul class="rows">${patrols}</ul>
    <h4>Size and bag limits</h4>
    <p class="note">Proposed for all four islands, each as strict as Hawaiʻi's or stricter.</p>
    <ul class="rows">${limits}</ul>`;
}

/** An indicator's page: its reading and why, and the communities it is read from. */
export function indicatorHtml(indicator: Indicator, communities: readonly CommunitySummary[]): string {
  const mine = communities.filter((c) => indicator.communities.includes(c.id));
  return `<h3>${escapeHtml(indicator.label)}</h3>
    <p class="reading">${escapeHtml(indicator.value)} ${trendHtml(indicator.trend)} ${levelHtml(indicator.level, indicator.unknownAs)}</p>
    <p class="why">${escapeHtml(capital(indicator.why))}.</p>
    ${mine.length > 0 ? `<ul class="rows">${mine.map((c) => `<li>${opens({ kind: 'community', id: c.id }, `<span class="row-name">${escapeHtml(c.name)}</span>${levelHtml(c.level, c.unknownAs)} ${trendHtml(c.trend)}`, 'row')}</li>`).join('')}</ul>` : ''}`;
}

/**
 * A community's page (the brief's second layer): what it does, how it stands, its threats, each member and what is
 * known of it (each opening its species). Not the model's own figures (M163, the user's word: "no", asked whether the
 * card's old rows should stay folded beneath).
 */
export function communityHtml(c: CommunitySummary): string {
  const members = c.members.map((m) => `<li>${opens({ kind: 'species', id: m.id }, `<span class="row-name">${escapeHtml(m.name)}</span><span class="known ${m.known.state.replace(' ', '-')}">${KNOWN_WORDS[m.known.state]}</span> ${trendHtml(m.known.trend)}<span class="row-note">${escapeHtml(m.known.text)}</span>`, 'row')}</li>`).join('');
  return `<h3>${escapeHtml(c.name)}</h3>
    <p class="reading">${levelHtml(c.level, c.unknownAs)} ${trendHtml(c.trend)}</p>
    <p class="why">${escapeHtml(c.role)}</p>
    ${c.threats.length > 0 ? `<h4>Threats</h4><ul class="threats">${c.threats.map((t) => `<li>${escapeHtml(capital(t))}</li>`).join('')}</ul>` : ''}
    <h4>Species</h4><ul class="rows">${members}</ul>`;
}

/**
 * A species' page (the brief's third layer): its names and community, what is known of it, and its rows as
 * `speciesRecord` gives them (who counts it, what it eats and what eats it, its threats, its life in the game); its
 * scientific name the subtitle's, not a row again; its picture, where the HUD gives one (the bullethead's, M163), under
 * what is known.
 */
export function speciesHtml(d: SpeciesDetail, picture = ''): string {
  return `<h3>${escapeHtml(d.title)}</h3>
    <p class="scientific"><i>${escapeHtml(d.record.scientific)}</i> · ${escapeHtml(d.community)}</p>
    <p class="reading"><span class="known ${d.known.state.replace(' ', '-')}">${KNOWN_WORDS[d.known.state]}</span> ${trendHtml(d.known.trend)} ${escapeHtml(d.known.text)}</p>
    ${picture}
    <dl class="stacked">${d.rows.filter(([label]) => label !== 'Scientific name').map(([label, text]) => `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(capital(text))}</dd>`).join('')}</dl>`;
}

/** The field guide's search box, built once while the guide is open (a day's redraw fills only its list, so the box keeps its focus and its words). */
export function guideSearchHtml(search: string): string {
  return `<h3>Field guide</h3><input class="guide-search" type="search" placeholder="Search by any name" aria-label="Search the field guide" value="${escapeHtml(search)}"><div class="guide-results"></div>`;
}

/** The field guide's list: every species by community, each opening its page; a word where none matches. */
export function guideHtml(groups: readonly { community: string; entries: readonly GuideEntry[] }[]): string {
  if (groups.length === 0) return '<p class="note">No species by that name.</p>';
  return groups.map((g) => `<h4>${escapeHtml(g.community)}</h4><ul class="rows">${g.entries.map((e) => `<li>${opens({ kind: 'species', id: e.id }, `<span class="row-name">${escapeHtml(e.title)}</span><span class="known ${e.state.replace(' ', '-')}">${KNOWN_WORDS[e.state]}</span><span class="row-note"><i>${escapeHtml(e.scientific)}</i></span>`, 'row')}</li>`).join('')}</ul>`).join('');
}

/** A page's name, for the way back to it. */
export function viewName(view: CardView, names: { indicator(id: IndicatorId): string; community(id: CommunityId): string; species(id: string): string }): string {
  switch (view.kind) {
    case 'overview': return 'Home';
    case 'details': return 'Sanctuary details';
    case 'coral': return 'The coral report';
    case 'survey': return 'Survey';
    case 'findings': return 'Findings';
    case 'respond': return 'Protect the reef';
    case 'indicator': return names.indicator(view.id);
    case 'community': return names.community(view.id);
    case 'species': return names.species(view.id);
    case 'conditions': return 'Conditions';
    case 'management': return 'Management';
    case 'operations': return 'Staff and accounts';
    case 'campaign': return 'The campaign';
    case 'review': return 'The annual review';
    case 'guide': return 'Field guide';
    case 'area': return ISLAND_NAMES[view.id];
    case 'plan': return 'Operations';
    case 'report': return 'The month\'s report';
    case 'build': return 'Build';
    case 'health': return 'Reef health';
    case 'first-survey': return 'Your first survey';
    case 'month': return 'The month in review';
    case 'result': return 'Done';
  }
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
